/**
 * Las solicitudes del equipo que la jefatura (o su suplente) tiene que resolver.
 * Va arriba de «Mi equipo»: es lo único de esa pantalla que pide acción.
 *
 * Existe porque la aprobación vivía solo en el ERP, y una jefatura con cuenta
 * del portal —sin usuario del ERP, para no consumir licencias— no tenía dónde
 * aprobar ni se enteraba (zenlatam, 2026-10-07).
 *
 * - **Aprobar** es un clic: es lo esperado y RRHH puede anular unas vacaciones
 *   aprobadas desde el ERP si hubo un error.
 * - **Rechazar** abre un diálogo con el motivo obligatorio, porque el trabajador
 *   lo recibe por correo.
 * - Un 409 es que RRHH ya la resolvió: se avisa y la lista se refresca sola.
 *
 * ⚠️ Lo que no pide acción no se anuncia: sin pendientes, el bloque no aparece.
 */
import { useState } from 'react'
import { Check, Inbox, X } from 'lucide-react'
import { Avatar } from '@/components/ds/Avatar'
import { Button } from '@/components/ds/Button'
import { GlassPanel } from '@/components/ds/GlassPanel'
import { Modal } from '@/components/ds/Modal'
import { useToast } from '@/components/Toast'
import { extractErrorMessage } from '@/lib/errorUtils'
import {
  useDecideTeamRequest, usePortalTeamRequests, type SolicitudDelEquipo,
} from '../hooks/useEmployeePortal'
import { dias, fechaCorta } from './formatos'

function cuando(s: SolicitudDelEquipo): string {
  return s.desde === s.hasta
    ? `el ${fechaCorta(s.desde)}`
    : `del ${fechaCorta(s.desde)} al ${fechaCorta(s.hasta)}`
}

function cuantos(s: SolicitudDelEquipo): string {
  const n = Number(s.dias)
  const unidad = n === 1 ? 'día' : 'días'
  // Las vacaciones y los beneficios de días se cuentan en hábiles; los demás
  // permisos, en corridos.
  return `${dias(s.dias)} ${unidad}${s.kind === 'vacation' || s.saldo_beneficio ? ' hábiles' : ''}`
}

/** «Saldo: 1 de 3 días disponibles · 1 en trámite (incluye esta)». */
function saldoDelBeneficio(s: SolicitudDelEquipo): string | null {
  const b = s.saldo_beneficio
  if (!b) return null
  const total = Number(b.asignado) + Number(b.arrastre)
  let texto = `Saldo: ${dias(b.disponible)} de ${dias(String(total))} ${total === 1 ? 'día disponible' : 'días disponibles'}`
  if (Number(b.en_tramite) > 0) texto += ` · ${dias(b.en_tramite)} en trámite (incluye esta)`
  if (Number(b.excedido) > 0) texto += ` · ya excedido en ${dias(b.excedido)}`
  return texto
}

export function SolicitudesPorResolver() {
  const { data } = usePortalTeamRequests()
  const decidir = useDecideTeamRequest()
  const toast = useToast()
  const [enCurso, setEnCurso] = useState<string | null>(null)
  const [aRechazar, setARechazar] = useState<SolicitudDelEquipo | null>(null)

  const solicitudes = data?.results ?? []
  if (solicitudes.length === 0) return null

  const aprobar = (s: SolicitudDelEquipo) => {
    setEnCurso(s.id)
    decidir.mutate({ solicitud: s, accion: 'approve' }, {
      onSuccess: () => toast.success(`Solicitud de ${s.empleado.nombre} aprobada.`),
      onError: (e: unknown) => toast.error(extractErrorMessage(e, 'No se pudo aprobar la solicitud.')),
      onSettled: () => setEnCurso(null),
    })
  }

  return (
    <GlassPanel as="section" aria-labelledby="solicitudes-por-resolver" className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Inbox size={18} className="text-warning-content" aria-hidden="true" />
        <h2 id="solicitudes-por-resolver" className="font-semibold text-content">
          Solicitudes por resolver
        </h2>
        <span className="rounded-full bg-warning-surface border border-warning-border px-2 text-xs font-semibold text-warning-content tabular-nums">
          {solicitudes.length}
        </span>
      </div>

      <ul className="divide-y divide-divider">
        {solicitudes.map(s => (
          <li key={`${s.kind}-${s.id}`} className="flex flex-wrap items-center gap-3 py-3">
            <Avatar nombre={s.empleado.nombre} fotoUrl={s.empleado.foto_url} tamano="sm" decorativa />
            <div className="flex-1 min-w-[14rem]">
              <p className="text-sm text-content">
                <span className="font-medium">{s.empleado.nombre}</span>
                <span className="text-content-muted"> · {s.tipo}</span>
              </p>
              <p className="text-xs text-content-muted tabular-nums">
                {cuantos(s)} {cuando(s)}
              </p>
              {saldoDelBeneficio(s) && (
                <p className="text-xs text-content-muted mt-0.5 tabular-nums">{saldoDelBeneficio(s)}</p>
              )}
              {s.motivo && <p className="text-xs text-content-subtle mt-0.5">Motivo: {s.motivo}</p>}
              {s.estado === 'pending_second' && (
                <p className="text-xs text-content-subtle mt-0.5">
                  Primera aprobación: {s.primera_aprobacion || '—'}. Falta la segunda.
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="secondary" icon={<X size={14} aria-hidden="true" />}
                disabled={enCurso !== null} onClick={() => setARechazar(s)}
                aria-label={`Rechazar la solicitud de ${s.empleado.nombre}`}>
                Rechazar
              </Button>
              <Button size="sm" variant="success-solid" icon={<Check size={14} aria-hidden="true" />}
                loading={enCurso === s.id} disabled={enCurso !== null && enCurso !== s.id}
                onClick={() => aprobar(s)}
                aria-label={`Aprobar la solicitud de ${s.empleado.nombre}`}>
                Aprobar
              </Button>
            </div>
          </li>
        ))}
      </ul>

      {aRechazar && (
        <DialogoDeRechazo solicitud={aRechazar} onClose={() => setARechazar(null)} />
      )}
    </GlassPanel>
  )
}

function DialogoDeRechazo({ solicitud, onClose }: { solicitud: SolicitudDelEquipo; onClose: () => void }) {
  const decidir = useDecideTeamRequest()
  const toast = useToast()
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState<string | null>(null)

  const rechazar = () => {
    if (!motivo.trim()) { setError('Falta el motivo del rechazo.'); return }
    setError(null)
    decidir.mutate({ solicitud, accion: 'reject', motivo: motivo.trim() }, {
      onSuccess: () => {
        toast.success(`Solicitud de ${solicitud.empleado.nombre} rechazada.`)
        onClose()
      },
      onError: (e: unknown) => setError(extractErrorMessage(e, 'No se pudo rechazar la solicitud.')),
    })
  }

  return (
    <Modal
      open onClose={onClose} size="sm" dismissOnScrimClick={false}
      title="Rechazar solicitud"
      subtitle={`${solicitud.empleado.nombre} · ${solicitud.tipo} ${cuando(solicitud)}`}
      actions={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button variant="danger-solid" loading={decidir.isPending} onClick={rechazar}>Rechazar</Button>
        </div>
      }
    >
      <div className="space-y-2">
        <label htmlFor="rechazo-motivo" className="block text-xs font-medium text-content-muted">
          Motivo *
        </label>
        <textarea
          id="rechazo-motivo" value={motivo} onChange={e => setMotivo(e.target.value)} rows={3}
          className="w-full px-3 py-2 text-sm border border-input-border rounded-lg bg-input-bg text-content resize-none"
        />
        <p className="text-xs text-content-subtle">El motivo le llega por correo a quien pidió la solicitud.</p>
        {error && <p role="alert" className="text-sm text-danger-solid">{error}</p>}
      </div>
    </Modal>
  )
}
